import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import Calendar from "../../../components/Calendar";
import CalendarHeader from "./CalendarHeader";
import CustomDayCell from "./CustomDayCell";

import {
    eachDayOfInterval,
    endOfISOWeek,
    getISOWeek,
    getISOWeekYear,
    startOfISOWeek,
} from "date-fns";
import { AuthContext } from "../../../contexts/AuthContext";
import { getTradesSessions } from "../../../services/api/tradingSessions";
import { getValidatedWeeksByUserId } from "../../../services/api/validatedWeek";
import { NoTradesSession } from "../../../types/NoTradesSession";
import { Session } from "../../../types/Session";
import { TradingStyleValue } from "../../../types/enums/TradingStyle";
import { ValidatedWeek } from "../../../types/ValidatedWeek";
import {
    getEnNumericMonthYear,
    getFormattedDateInput,
    parseInputDateToLocal
} from "../../../utils/date";
import ValidWeekFormModal from "../Modals/ValidWeekFormModal";

import RecapModal from "../Modals/RecapModal";
import { useJournalSettings } from "../../../services/journalSettings";
type CalendarSessionsProps = {
    userId: string;
    hidePerformance: boolean;
    className?: string;
    month: Date;
    setMonth: (date: Date) => void;
    effectiveStartDate: Date;
    effectiveEndDate: Date;
    timeslotFilter?: string;
    challengeFilter?: string;
    tradingStyleFilter?: TradingStyleValue;
    setTimeslotFilter?: (filter?: string) => void;
    timeslotCategories: {
        morning: string[];
        afternoon: string[];
        evening: string[];
    };
    modalDayDetailsState: {
        open: boolean;
        date: string | undefined;
        sessions: (Session | NoTradesSession)[] | undefined;
    };
    setModalDayDetailsState: React.Dispatch<
        React.SetStateAction<{
            open: boolean;
            date: string | undefined;
            sessions: (Session | NoTradesSession)[] | undefined;
        }>
    >;
    handleDayDetailsOpen: (
        date: string | undefined,
        sessions: (Session | NoTradesSession)[]
    ) => void;
    /** Dashboard perso : clic sur jour ouvré sans session → choix Trades / No trades */
    onEmptyWeekdayClick?: (date: Date) => void;
    /** Si défini avec onEmptyWeekdayClick : jour cliquable seulement quand true (sinon pas de curseur / pas d’action). */
    isEmptyCalendarDayAddAllowed?: (date: Date) => boolean;
    tradingSessionModalStateOpen: boolean;
    noTradeModalStateOpen: boolean;
    confirmModalOpen: boolean;
};

const CalendarSessions: FC<CalendarSessionsProps> = ({
    userId,
    hidePerformance,
    className,
    month,
    setMonth,
    effectiveStartDate,
    effectiveEndDate,
    timeslotFilter,
    challengeFilter,
    tradingStyleFilter,
    timeslotCategories,
    modalDayDetailsState,
    setModalDayDetailsState,
    handleDayDetailsOpen,
    onEmptyWeekdayClick,
    isEmptyCalendarDayAddAllowed,
    tradingSessionModalStateOpen,
    noTradeModalStateOpen,
    confirmModalOpen,
}) => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const { getUserIdFromQuery, getIsOwnDashboard, hasPermission } =
        useContext(AuthContext)!;
    const { challengeMoneyManagement } = useJournalSettings();

    const [validWeekFormModalState, setValidWeekFormModalState] = useState<{
        open: boolean;
        weekNumber: number | undefined;
        year: number | undefined;
        weekRecap?: { weekResult?: string; breakdownByTimeslot?: { timeslot: string; total: number }[] };
    }>({ open: false, weekNumber: undefined, year: undefined });

    const [recapModalState, setRecapModalState] = useState<{
        weekNumber: number | undefined;
        weekResult: string | undefined;
        year: number | undefined;
    }>({ weekNumber: undefined, weekResult: undefined, year: undefined });

    const [validatedWeeks, setValidatedWeeks] = useState<
        { [key: string]: ValidatedWeek }[]
    >([]);

    const [sessions, setSessions] = useState<{
        [key: string]: (Session | NoTradesSession)[];
    }>({});
    const emptySessions = useMemo<(Session | NoTradesSession)[]>(() => [], []);

    // Le filtrage par créneau se fait maintenant côté serveur

    /**
     * Plage API élargie aux semaines ISO complètes : si on ne charge que [1er ; dernier jour du mois],
     * une semaine à cheval sur deux mois n’a qu’une partie des jours en base locale → perf hebdo fausse.
     * On fetch donc de startOfISOWeek(effectiveStart) à endOfISOWeek(effectiveEnd). L’affichage “mois”
     * (totaux header, jours hors plage) reste basé sur effectiveStartDate / effectiveEndDate dans CustomDayCell.
     */
    const sessionsFetchStartDate = useMemo(
        () => startOfISOWeek(effectiveStartDate),
        [effectiveStartDate]
    );
    const sessionsFetchEndDate = useMemo(
        () => endOfISOWeek(effectiveEndDate),
        [effectiveEndDate]
    );

    const weeklyTotals = useMemo(() => {
        const totals: { [key: string]: number } = {};
        const roundCurrency = (value: number) =>
            Math.round((value + Number.EPSILON) * 100) / 100;

        Object.entries(sessions).forEach(([dateString, daySessions]) => {
            if (!daySessions || daySessions.length === 0) return;

            const d = parseInputDateToLocal(dateString);
            if (!d) return;
            const weekNumber = getISOWeek(d);
            const year = getISOWeekYear(d);
            const key = `${year}-W${weekNumber}`;

            const dayTotal = daySessions
                // Exclude challenge sessions from weekly totals
                .filter(
                    (session) =>
                        (session as Session).moneyManagement !==
                        challengeMoneyManagement
                )
                .reduce(
                    (acc, session) => roundCurrency(acc + ((session as Session).total || 0)),
                    0
                );

            if (!totals[key]) totals[key] = 0;
            totals[key] = roundCurrency(totals[key] + dayTotal);
        });

        return totals;
    }, [sessions, challengeMoneyManagement]);

    /** Détail par créneau pour chaque semaine (pour la modale récap) */
    const weeklyBreakdownByTimeslot = useMemo(() => {
        const roundCurrency = (value: number) =>
            Math.round((value + Number.EPSILON) * 100) / 100;
        const byWeek: Record<string, Record<string, number>> = {};

        Object.entries(sessions).forEach(([dateString, daySessions]) => {
            if (!daySessions || daySessions.length === 0) return;

            const d = parseInputDateToLocal(dateString);
            if (!d) return;
            const weekNum = getISOWeek(d);
            const weekYear = getISOWeekYear(d);
            const key = `${weekYear}-W${weekNum}`;

            if (!byWeek[key]) byWeek[key] = {};

            daySessions
                .filter(
                    (session) =>
                        (session as Session).moneyManagement !==
                        challengeMoneyManagement
                )
                .forEach((session) => {
                    const s = session as Session;
                    const slot = s.timeslot || "";
                    if (!slot) return;
                    const total = s.total ?? 0;
                    byWeek[key][slot] = roundCurrency(
                        (byWeek[key][slot] ?? 0) + total
                    );
                });
        });

        const result: Record<string, { timeslot: string; total: number }[]> = {};
        Object.entries(byWeek).forEach(([weekKey, slotTotals]) => {
            result[weekKey] = Object.entries(slotTotals).map(([timeslot, total]) => ({
                timeslot,
                total,
            }));
        });
        return result;
    }, [sessions, challengeMoneyManagement]);

    const handleValidWeekFormModalOpen = (
        weekNumber: number,
        year: number,
        weekResult?: string,
        breakdownByTimeslot?: { timeslot: string; total: number }[]
    ) => {
        setValidWeekFormModalState({
            open: true,
            weekNumber,
            year,
            weekRecap:
                weekResult != null || (breakdownByTimeslot?.length ?? 0) > 0
                    ? { weekResult, breakdownByTimeslot }
                    : undefined,
        });
    };

    const handleValidWeekFormModalClose = () => {
        setValidWeekFormModalState({
            ...validWeekFormModalState,
            open: false,
            weekNumber: undefined,
            weekRecap: undefined,
        });
    };

    // New calendar
    useEffect(() => {
        if (tradingSessionModalStateOpen === true) {
            return;
        }

        if (confirmModalOpen === true) {
            return;
        }

        // Déterminer les créneaux à envoyer
        let timeslots: string[] | undefined;
        if (timeslotFilter) {
            timeslots = timeslotCategories[timeslotFilter as keyof typeof timeslotCategories];
        }

        // Map challengeFilter to isChallenge boolean
        let isChallenge: boolean | undefined;
        if (challengeFilter === "only") {
            isChallenge = true;
        } else if (challengeFilter === "exclude") {
            isChallenge = false;
        }

        getTradesSessions({
            userId,
            start: getFormattedDateInput(sessionsFetchStartDate),
            end: getFormattedDateInput(sessionsFetchEndDate),
            timeslots,
            isChallenge,
            tradingStyle: tradingStyleFilter,
        }).then((data) => {
            if (data) {
                setSessions(data.sessions);
            }
        });
    }, [
        userId,
        month,
        sessionsFetchStartDate,
        sessionsFetchEndDate,
        getIsOwnDashboard,
        tradingSessionModalStateOpen,
        noTradeModalStateOpen,
        getUserIdFromQuery,
        confirmModalOpen,
        validWeekFormModalState.open,
        timeslotFilter,
        challengeFilter,
        tradingStyleFilter,
        timeslotCategories,
    ]);

    // Update DayDetails modal sessions when sessions or date change
    useEffect(() => {
        if (!modalDayDetailsState.open || !modalDayDetailsState.date) {
            return;
        }

        const dateKey = modalDayDetailsState.date;
        const nextSessions = sessions[dateKey] ?? emptySessions;
        setModalDayDetailsState((prevState) =>
            prevState.sessions === nextSessions
                ? prevState
                : (!sessions[dateKey] &&
                      prevState.sessions &&
                      prevState.sessions.length === 0)
                ? prevState
                : {
                      ...prevState,
                      sessions: nextSessions,
                  },
        );
    }, [
        modalDayDetailsState.open,
        modalDayDetailsState.date,
        setModalDayDetailsState,
        sessions,
        emptySessions,
    ]);

    useEffect(() => {
        if (
            validWeekFormModalState.open === true ||
            (!getIsOwnDashboard() &&
                !hasPermission("admin-validated-weeks", "read"))
        ) {
            return;
        }

        getValidatedWeeksByUserId(getUserIdFromQuery()!)
            .then((data) => {
                if (data) setValidatedWeeks(data);
            })
            .catch(() => {
                setValidatedWeeks([]);
            });
    }, [
        getUserIdFromQuery,
        validWeekFormModalState.open,
        getIsOwnDashboard,
        hasPermission,
    ]);

    // Don't override filter dates based on month - use the passed filter dates
    const handleChangeCurrentMonth = (date: Date) => {
        setMonth(date);
        queryParams.set("selectedMonth", getEnNumericMonthYear(date));

        const newRelativePathQuery = `${window.location.pathname}${
            queryParams.toString() ? "?" + queryParams.toString() : ""
        }`;
        window.history.replaceState(null, "", newRelativePathQuery);
    };

    const openRecapModal = (weekNumber: number, weekResult: string, year: number) => {
        setRecapModalState({ weekNumber, weekResult, year });
    };

    const refetchValidatedWeeks = () => {
        getValidatedWeeksByUserId(getUserIdFromQuery()!)
            .then((data) => {
                if (data) setValidatedWeeks(data);
            })
            .catch(() => {
                setValidatedWeeks([]);
            });
    };

    const shouldHideWeekRow = (weekDates: Date[]) => {
        if (!weekDates || weekDates.length === 0) return false;
        const hasTradingDayInMonth = weekDates.some(
            (date) =>
                date.getFullYear() === month.getFullYear() &&
                date.getMonth() === month.getMonth() &&
                date.getDay() >= 1 &&
                date.getDay() <= 5
        );
        return !hasTradingDayInMonth;
    };

    return (
        <>
            <ValidWeekFormModal
                open={validWeekFormModalState.open}
                onClose={handleValidWeekFormModalClose}
                weekNumber={validWeekFormModalState.weekNumber}
                year={validWeekFormModalState.year}
                weekRecap={validWeekFormModalState.weekRecap}
            />
            <RecapModal
                open={!!recapModalState.weekNumber}
                onClose={() => {
                    setRecapModalState({
                        weekNumber: undefined,
                        weekResult: undefined,
                        year: undefined,
                    });
                }}
                onDeleted={refetchValidatedWeeks}
                weekNumber={recapModalState.weekNumber}
                weekResult={recapModalState.weekResult}
                year={recapModalState.year}
                breakdownByTimeslot={
                    recapModalState.weekNumber != null &&
                    recapModalState.year != null
                        ? (weeklyBreakdownByTimeslot[
                              `${recapModalState.year}-W${recapModalState.weekNumber}`
                          ] ?? [])
                        : []
                }
            />
            <Calendar
                className={className}
                header={
                    <CalendarHeader
                        sessions={sessions}
                        hidePerformance={hidePerformance}
                        currentMonth={month}
                        setCurrentMonth={handleChangeCurrentMonth}
                    />
                }
                dayPickerProps={{
                    month,
                    components: {
                        Week: (weekProps) => {
                            const rawWeekDays: any[] =
                                weekProps?.week?.days ||
                                (weekProps as any)?.dates ||
                                (weekProps as any)?.days ||
                                [];
                            const weekDates: Date[] = rawWeekDays
                                .map((day) => day?.date ?? day)
                                .filter((day) => day instanceof Date);
                            const shouldHide = shouldHideWeekRow(weekDates);
                            const { children, ...rest } = weekProps as any;
                            return (
                                <tr
                                    {...rest}
                                    style={
                                        shouldHide
                                            ? { display: "none" }
                                            : rest.style
                                    }
                                >
                                    {children}
                                </tr>
                            );
                        },
                        Day: (props) => {
                            const dateObj = props.day.date;
                            const weekNumber = getISOWeek(dateObj);
                            const key = `${getISOWeekYear(dateObj)}-W${weekNumber}`;
                            const isSaturday = dateObj.getDay() === 6;

                            const weekHasAnySession = isSaturday
                                ? eachDayOfInterval({
                                      start: startOfISOWeek(dateObj),
                                      end: endOfISOWeek(dateObj),
                                  }).some(
                                      (d) =>
                                          (sessions[
                                              getFormattedDateInput(d)
                                          ]?.length ?? 0) > 0
                                  )
                                : undefined;

                            return (
                                <CustomDayCell
                                    {...props}
                                    hidePerformance={hidePerformance}
                                    startDate={effectiveStartDate}
                                    endDate={effectiveEndDate}
                                    handleDayDetailsOpen={handleDayDetailsOpen}
                                    handleValidWeekFormModalOpen={
                                        handleValidWeekFormModalOpen
                                    }
                                    sessions={
                                        sessions[
                                            getFormattedDateInput(
                                                props.day.date
                                            )
                                        ] || []
                                    }
                                    weekBreakdownByTimeslot={
                                        weeklyBreakdownByTimeslot[key] ?? []
                                    }
                                    validatedWeeks={validatedWeeks}
                                    displayedMonth={month}
                                    weeklyTotal={
                                        isSaturday
                                            ? weeklyTotals[key]
                                            : undefined
                                    }
                                    weekHasAnySession={weekHasAnySession}
                                    openRecapModal={openRecapModal}
                                    onEmptyWeekdayClick={onEmptyWeekdayClick}
                                    isEmptyCalendarDayAddAllowed={
                                        isEmptyCalendarDayAddAllowed
                                    }
                                />
                            );
                        },
                    },
                }}
            />
        </>
    );
};

export default CalendarSessions;
